import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Grid3X3, 
  List, 
  Eye,
  Edit3,
  Star, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Palette,
  User,
  Building2,
  BookOpen,
  Image,
  X,
  Globe,
  ArrowLeft,
  Printer,
  Zap,
  Upload,
  Check,
  ExternalLink,
  FileImage
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import LoadingSpinner from '../ui/LoadingSpinner';
import './CuratorArtworks.css';

// Helper to add f_auto transformation for format conversion (HEIC -> WebP/JPEG)
// without cropping or resizing - preserves original aspect ratio
const getDisplayImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  if (imageUrl.includes('cloudinary.com') && imageUrl.includes('/upload/')) {
    return imageUrl.replace('/upload/', '/upload/f_auto,q_auto/');
  }
  return imageUrl;
};

const CuratorArtworks = ({ showAll: initialShowAll = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedOrganization } = useOutletContext() || {};
  
  // URL parameter for pre-selecting a collection
  const addToCollectionId = searchParams.get('addToCollection');
  const [preSelectedCollection, setPreSelectedCollection] = useState(null);
  
  // Browse mode state - toggle between curated and all artworks
  const [browseAll, setBrowseAll] = useState(initialShowAll || !!addToCollectionId);
  
  // State management
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const searchTimeoutRef = useRef(null);
  const [collectionFilter, setCollectionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [accessibleCollections, setAccessibleCollections] = useState([]);

  // Collection selector state
  const [collectionSelectorOpen, setCollectionSelectorOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [currentCollections, setCurrentCollections] = useState([]);
  const [collectionsViewOpen, setCollectionsViewOpen] = useState(false);
  const [assigningCollectionId, setAssigningCollectionId] = useState(null);
  const [collectionSelectorError, setCollectionSelectorError] = useState(null);
  
  // Fullscreen image modal state
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // Print quality modal state
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printModalArtwork, setPrintModalArtwork] = useState(null);
  const [printStatus, setPrintStatus] = useState(null);
  const [loadingPrintStatus, setLoadingPrintStatus] = useState(false);
  const [scanningQuality, setScanningQuality] = useState(false);
  const [uploadingHighRes, setUploadingHighRes] = useState(false);
  const [analyzingColors, setAnalyzingColors] = useState(false);
  const highResInputRef = useRef(null);

  // Print quality constants - 240 DPI for print sizes
  const PRINT_DIMENSIONS_240DPI = {
    '5x7': { minPx: 1200, label: '5×7"' },
    '8x10': { minPx: 1920, label: '8×10"' },
    '11x14': { minPx: 2640, label: '11×14"' },
    '12x12': { minPx: 2880, label: '12×12"' },
    '16x20': { minPx: 3840, label: '16×20"' },
    '18x18': { minPx: 4320, label: '18×18"' },
    '20x30': { minPx: 4800, label: '20×30"' }
  };

  // Get current user
  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  };

  // Open collection selector modal
  const openCollectionSelector = async (artwork) => {
    setSelectedArtwork(artwork);
    setCollectionSelectorOpen(true);
    setCollectionSelectorError(null);
    setAssigningCollectionId(null);
    setCurrentCollections([]);
    
    // Fetch current collections for this artwork when opening selector
    try {
      const { data: collections, error } = await supabase
        .from('collection_vc_mapping')
        .select(`
          collection_id,
          collections!inner(title, description)
        `)
        .eq('vc_id', artwork.vc_id);

      if (error) {
        console.error('Error fetching collections for artwork:', error);
        setCurrentCollections([]);
        return;
      }
      
      // Transform the data to get collection details and sort by title
      const transformedCollections = collections
        .map(item => ({
          collection_id: item.collection_id,
          title: item.collections.title,
          description: item.collections.description
        }))
        .sort((a, b) => a.title.localeCompare(b.title)); // Sort alphabetically by title
      
      setCurrentCollections(transformedCollections);
    } catch (error) {
      console.error('Error in openCollectionSelector:', error);
      setCurrentCollections([]);
      setCollectionSelectorError('Unable to load collections for this artwork.');
    }
  };

  const closeCollectionSelector = () => {
    setCollectionSelectorOpen(false);
    setSelectedArtwork(null);
    setCurrentCollections([]);
    setCollectionSelectorError(null);
    setAssigningCollectionId(null);
  };

  // Direct assignment when pre-selected collection exists
  const handleDirectAssignment = async (artwork) => {
    if (!preSelectedCollection?.collection_id) {
      openCollectionSelector(artwork);
      return;
    }

    setAssigningCollectionId(preSelectedCollection.collection_id);
    
    try {
      // Check if already assigned
      const { data: existing } = await supabase
        .from('collection_vc_mapping')
        .select('mapping_id')
        .eq('collection_id', preSelectedCollection.collection_id)
        .eq('vc_id', artwork.vc_id)
        .maybeSingle();
      
      if (existing) {
        // Already assigned, show brief notification
        alert(`"${artwork.title}" is already in ${preSelectedCollection.title}`);
        setAssigningCollectionId(null);
        return;
      }

      const { error } = await supabase
        .from('collection_vc_mapping')
        .upsert(
          {
            collection_id: Number(preSelectedCollection.collection_id),
            vc_id: artwork.vc_id,
            accepted: true,
            order_num: 100
          },
          { onConflict: 'vc_id,collection_id' }
        );

      if (error) {
        throw error;
      }

      // Show success feedback - brief visual indication
      await fetchArtworks();
    } catch (assignError) {
      console.error('Error assigning artwork to collection:', assignError);
      alert('Could not add the artwork. Please try again.');
    } finally {
      setAssigningCollectionId(null);
    }
  };

  const handleAssignToCollection = async (collectionId) => {
    if (!collectionId) {
      return;
    }

    if (!selectedArtwork) {
      return;
    }

    const alreadyAssigned = currentCollections.some(
      (collection) => Number(collection.collection_id) === Number(collectionId)
    );

    if (alreadyAssigned) {
      closeCollectionSelector();
      return;
    }

    setAssigningCollectionId(collectionId);
    setCollectionSelectorError(null);

    try {
      const { error } = await supabase
        .from('collection_vc_mapping')
        .upsert(
          {
            collection_id: Number(collectionId),
            vc_id: selectedArtwork.vc_id,
            accepted: true,
            order_num: selectedArtwork.order_num ?? 100
          },
          { onConflict: 'vc_id,collection_id' }
        );

      if (error) {
        throw error;
      }

      closeCollectionSelector();
      await fetchArtworks();
    } catch (assignError) {
      console.error('Error assigning artwork to collection:', assignError);
      setCollectionSelectorError('Could not add the artwork to that collection. Please try again.');
    } finally {
      setAssigningCollectionId(null);
    }
  };

  const openCollectionsView = async (artwork) => {
    setSelectedArtwork(artwork);
    
    // Fetch collections for this artwork
    try {
      const { data: collections, error } = await supabase
        .from('collection_vc_mapping')
        .select(`
          collection_id,
          collections!inner(title, description)
        `)
        .eq('vc_id', artwork.vc_id);

      if (error) {
        console.error('Error fetching collections:', error);
        setCurrentCollections([]);
      } else {
        const transformedCollections = collections
          .map(item => ({
            collection_id: item.collection_id,
            title: item.collections.title,
            description: item.collections.description
          }))
          .sort((a, b) => a.title.localeCompare(b.title));
        
        setCurrentCollections(transformedCollections);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
      setCurrentCollections([]);
    }
    
    setCollectionsViewOpen(true);
  };

  const closeCollectionsView = () => {
    setCollectionsViewOpen(false);
    setSelectedArtwork(null);
    setCurrentCollections([]);
  };

  // Navigate to artwork detail
  const handleArtworkClick = (vcId) => {
    navigate(`/curator/artworks/${vcId}`);
  };

  // Get curator's accessible collections for the selected organization
  const getAccessibleCollections = async () => {
    try {
      // If no organization selected, return empty
      if (!selectedOrganization?.organization_id) {
        return { collections: [] };
      }

      // Get public collections for the selected organization
      const { data: collections, error: collectionsError } = await supabase
        .from('collections')
        .select('collection_id, title, description, organization_id')
        .eq('organization_id', selectedOrganization.organization_id)
        .eq('access_control', 'Public');

      if (collectionsError) throw collectionsError;

      return { collections: collections || [] };
    } catch (error) {
      console.error('Error getting accessible collections:', error);
      return { collections: [] };
    }
  };

  // Fetch artworks data
  const fetchArtworks = useCallback(async () => {
    // Calculate pagination range inside the callback to ensure fresh values
    const paginationFrom = (currentPage - 1) * pageSize;
    const paginationTo = paginationFrom + pageSize - 1;
    
    // Only show full loading spinner on initial load
    if (!hasInitiallyLoaded) {
      setLoading(true);
    } else if (currentPage === 1) {
      // For filter/search changes, show subtle loading indicator
      setIsSearching(true);
    } else {
      setIsPageLoading(true);
    }
    setError(null);
    setLoadingProgress({ current: 0, total: 0 });
    
    try {
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Helper function to check if artwork matches search/price filters
      const matchesFilters = (artwork) => {
        if (!artwork || artwork.access_control !== 'Public') {
          return false;
        }

        if (debouncedSearchQuery.trim()) {
          const query = debouncedSearchQuery.toLowerCase();
          const title = (artwork.title || '').toLowerCase();
          const artistName = (artwork.users?.nickname || artwork.users?.handle || '').toLowerCase();
          const firstName = (artwork.users?.first_name || '').toLowerCase();
          const lastName = (artwork.users?.last_name || '').toLowerCase();

          if (!title.includes(query) && !artistName.includes(query) && 
              !firstName.includes(query) && !lastName.includes(query) &&
              !`${firstName} ${lastName}`.includes(query)) {
            return false;
          }
        }

        return true;
      };

      // BROWSE ALL MODE: Fetch all public artworks from the system
      if (browseAll) {
        console.log('Fetching ALL public artworks in the system');
        
        let allArtworks = [];
        const batchSize = 1000;
        let batchStart = 0;
        let hasMore = true;

        // Fetch all artworks in batches to overcome the 1000 row limit
        while (hasMore) {
          const { data: batch, error: batchError } = await supabase
            .from('vcs')
            .select(`
              vc_id,
              title,
              description,
              cover_image,
              price,
              width,
              height,
              depth,
              access_control,
              created_at,
              updated_at,
              location,
              users (
                uuid,
                nickname,
                first_name,
                last_name,
                handle
              )
            `)
            .eq('access_control', 'Public')
            .order('created_at', { ascending: false })
            .range(batchStart, batchStart + batchSize - 1);

          if (batchError) {
            console.error('Error fetching artworks batch:', batchError);
            throw batchError;
          }

          if (batch && batch.length > 0) {
            allArtworks = [...allArtworks, ...batch];
            batchStart += batchSize;
            hasMore = batch.length === batchSize;
          } else {
            hasMore = false;
          }
        }

        console.log('Fetched all artworks:', allArtworks.length);

        // Filter by search/price client-side
        let filteredArtworks = allArtworks.filter(matchesFilters);
        
        console.log('All artworks query result:', { total: allArtworks.length, filtered: filteredArtworks.length });

        // Sort
        const sortedArtworks = [...filteredArtworks].sort((a, b) => {
          if (sortBy === 'price') {
            const priceA = parseFloat(a.price) || 0;
            const priceB = parseFloat(b.price) || 0;
            return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
          }
          if (sortBy === 'title') {
            const titleA = (a.title || '').toLowerCase();
            const titleB = (b.title || '').toLowerCase();
            return sortOrder === 'asc' ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA);
          }
          if (sortBy === 'artist') {
            const artistA = (a.users?.nickname || a.users?.handle || '').toLowerCase();
            const artistB = (b.users?.nickname || b.users?.handle || '').toLowerCase();
            return sortOrder === 'asc' ? artistA.localeCompare(artistB) : artistB.localeCompare(artistA);
          }
          // Default to created_at
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });

        setTotalCount(sortedArtworks.length);
        const paginatedArtworks = sortedArtworks.slice(paginationFrom, paginationTo + 1);
        setLoadingProgress({ current: 0, total: paginatedArtworks.length });

        // Process artworks with additional details
        const artworksWithDetails = await Promise.all(
          paginatedArtworks.map(async (artwork, index) => {
            let medium = 'Unknown';
            try {
              const { data: attributes } = await supabase
                .from('attribute_vc_mapping')
                .select(`artwork_attributes!inner(title, type)`)
                .eq('vc_id', artwork.vc_id)
                .eq('artwork_attributes.type', 'medium')
                .limit(1);
              
              if (attributes && attributes.length > 0) {
                medium = attributes[0].artwork_attributes.title;
              }
            } catch (attrError) {
              console.warn(`Could not fetch medium for artwork ${artwork.vc_id}:`, attrError);
            }

            setLoadingProgress({ current: index + 1, total: paginatedArtworks.length });

            return {
              ...artwork,
              artist: artwork.users,
              medium,
              collectionTitle: null,
              collectionId: null,
              dimensions: `${artwork.width || 0}" × ${artwork.height || 0}"${artwork.depth ? ` × ${artwork.depth}"` : ''}`,
              formattedPrice: artwork.price ? `$${parseFloat(artwork.price).toLocaleString()}` : 'Not for sale'
            };
          })
        );

        setArtworks(artworksWithDetails);
        
        // Still fetch curator's accessible collections for the "Add to Collection" modal
        const { collections } = await getAccessibleCollections();
        setAccessibleCollections(collections);
        
        // Calculate stats for browseAll mode
        if (currentPage === 1) {
          const prices = sortedArtworks
            .map(a => a.price)
            .filter(price => price && price > 0)
            .map(price => parseFloat(price));
          const avgPrice = prices.length > 0 
            ? prices.reduce((sum, price) => sum + price, 0) / prices.length 
            : 0;

          setStats({
            total: sortedArtworks.length,
            collections: 0,
            avgPrice: avgPrice
          });
        }
        
        return; // Exit early for browseAll mode
      }

      // NORMAL MODE: Curator's accessible collections only
      // Get curator's accessible collections for the selected organization
      const { collections } = await getAccessibleCollections();
      setAccessibleCollections(collections);
      
      if (collections.length === 0) {
        setArtworks([]);
        setTotalCount(0);
        setStats({ total: 0, collections: 0, avgPrice: 0 });
        return;
      }

      const collectionIds = collections
        .map(c => Number(c.collection_id))
        .filter(id => !Number.isNaN(id));
      const showUnassignedOnly = collectionFilter === 'unassigned';
      console.log('Curator has access to collections:', collectionIds);

      // Get artworks from accessible collections
      let data = [];
      if (collectionIds.length > 0) {
        let artworkQuery = supabase
          .from('collection_vc_mapping')
          .select(`
            vc_id,
            collection_id,
            accepted,
            order_num,
            vcs (
              vc_id, 
              title, 
              description, 
              cover_image, 
              price, 
              width, 
              height, 
              depth, 
              access_control, 
              created_at, 
              updated_at, 
              uuid,
              users (
                uuid, 
                nickname, 
                first_name, 
                last_name, 
                handle
              )
            ),
            collections (
              collection_id,
              title
            )
          `)
          .in('collection_id', collectionIds);

        // Note: Show all artworks regardless of accepted status - curators need to see all submissions
        // The accepted field is used on the collection detail page for approval workflow

        // Apply collection filter
        if (!showUnassignedOnly && collectionFilter !== 'all') {
          artworkQuery = artworkQuery.eq('collection_id', parseInt(collectionFilter));
        }

        // Note: We'll filter by access_control and other filters after fetching 
        // since cross-table filtering in Supabase joins can be tricky

        // Apply sorting and pagination
        // Note: Complex sorting on joined tables can be tricky, so let's use basic sorting
        // Default sort by collection_vc_mapping created_at or order_num
        if (sortBy === 'created_at') {
          // Use the mapping table's order for now since cross-table sorting is complex
          artworkQuery = artworkQuery.order('order_num', { ascending: sortOrder === 'asc' });
        } else {
          // For other sorts, we'll handle them in JavaScript after fetching
          artworkQuery = artworkQuery.order('order_num', { ascending: false });
        }

        const { data: collectionData, error } = await artworkQuery;

        if (error) {
          console.error('Error fetching artworks:', error);
          throw error;
        }

        console.log('Curator artworks query result:', { data: collectionData, count: collectionData?.length });
        data = collectionData || [];
      }

      // Filter data in JavaScript since cross-table filtering in Supabase is complex
      let filteredData = data?.filter(mapping => {
        const artwork = mapping.vcs;

        if (showUnassignedOnly) {
          return false;
        }

        if (!matchesFilters(artwork)) {
          return false;
        }

        return true;
      }) || [];

      // All artworks come from organization's collections now
      const mergedEntries = filteredData.map(mapping => ({
        artwork: mapping.vcs,
        collectionTitle: mapping.collections?.title || 'Unknown Collection',
        collectionId: mapping.collection_id
      }));

      // Deduplicate by vc_id - keep first occurrence (with collection info if available)
      const seenVcIds = new Set();
      const uniqueEntries = mergedEntries.filter(entry => {
        const vcId = entry.artwork?.vc_id;
        if (!vcId || seenVcIds.has(vcId)) {
          return false;
        }
        seenVcIds.add(vcId);
        return true;
      });

      console.log('Filtered artworks:', {
        original: data?.length,
        filtered: filteredData.length,
        combined: mergedEntries.length,
        afterDedup: uniqueEntries.length
      });

      const totalEntries = uniqueEntries.length;

      const sortedEntries = [...uniqueEntries].sort((a, b) => {
        const artworkA = a.artwork;
        const artworkB = b.artwork;

        if (sortBy === 'price') {
          const priceA = parseFloat(artworkA.price) || 0;
          const priceB = parseFloat(artworkB.price) || 0;
          return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
        }

        if (sortBy === 'title') {
          const titleA = (artworkA.title || '').toLowerCase();
          const titleB = (artworkB.title || '').toLowerCase();
          return sortOrder === 'asc' ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA);
        }

        if (sortBy === 'artist') {
          const artistA = (artworkA.users?.nickname || artworkA.users?.handle || '').toLowerCase();
          const artistB = (artworkB.users?.nickname || artworkB.users?.handle || '').toLowerCase();
          return sortOrder === 'asc' ? artistA.localeCompare(artistB) : artistB.localeCompare(artistA);
        }

        // Default to created_at sorting
        const dateA = new Date(artworkA.created_at);
        const dateB = new Date(artworkB.created_at);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });

      setTotalCount(totalEntries);

      const paginatedEntries = sortedEntries.slice(paginationFrom, paginationTo + 1);

      setLoadingProgress({ current: 0, total: paginatedEntries.length });

      // Process artworks with additional details for the current page
      const paginatedArtworksWithDetails = await Promise.all(
        paginatedEntries.map(async ({ artwork, collectionTitle, collectionId }, index) => {
          let medium = 'Unknown';
          try {
            const { data: attributes } = await supabase
              .from('attribute_vc_mapping')
              .select(`artwork_attributes!inner(title, type)`)
              .eq('vc_id', artwork.vc_id)
              .eq('artwork_attributes.type', 'medium')
              .limit(1);
            
            if (attributes && attributes.length > 0) {
              medium = attributes[0].artwork_attributes.title;
            }
          } catch (attrError) {
            console.warn(`Could not fetch medium for artwork ${artwork.vc_id}:`, attrError);
          }

          setLoadingProgress({ current: index + 1, total: paginatedEntries.length });

          return {
            ...artwork,
            artist: artwork.users,
            medium,
            collectionTitle,
            collectionId,
            dimensions: `${artwork.width || 0}" × ${artwork.height || 0}"${artwork.depth ? ` × ${artwork.depth}"` : ''}`,
            formattedPrice: artwork.price ? `$${parseFloat(artwork.price).toLocaleString()}` : 'Not for sale'
          };
        })
      );

      setArtworks(paginatedArtworksWithDetails);

      // Calculate stats - get all filtered artworks for accurate count
      if (currentPage === 1) {
        let allData = [];
        if (collectionIds.length > 0) {
          const allArtworkQuery = supabase
            .from('collection_vc_mapping')
            .select(`
              vc_id,
              collection_id,
              accepted,
              vcs (
                vc_id, 
                title, 
                price, 
                access_control
              ),
              collections (
                collection_id,
                title
              )
            `)
            .in('collection_id', collectionIds);

          const { data: allCollectionData } = await allArtworkQuery;
          allData = allCollectionData || [];
        }

        // Filter all data for stats
        let allFilteredData = allData.filter(mapping => {
          const artwork = mapping.vcs;

          if (showUnassignedOnly) {
            return false;
          }
          
          if (artwork?.access_control !== 'Public') {
            return false;
          }
          
          if (!showUnassignedOnly && collectionFilter !== 'all') {
            if (mapping.collection_id !== parseInt(collectionFilter)) {
              return false;
            }
          }
          
          return true;
        });

        // Calculate stats from organization's collections only
        const totalArtworks = allFilteredData.length;
        const totalCollections = new Set(allFilteredData.map(d => d.collection_id)).size;
        const prices = allFilteredData
          .map(d => d.vcs?.price)
          .filter(price => price && price > 0)
          .map(price => parseFloat(price));
        const avgPrice = prices.length > 0 
          ? prices.reduce((sum, price) => sum + price, 0) / prices.length 
          : 0;

        setStats({
          total: totalArtworks,
          collections: totalCollections,
          avgPrice: avgPrice
        });
      }

    } catch (error) {
      console.error('Error fetching curator artworks:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      setIsPageLoading(false);
      setIsSearching(false);
      setHasInitiallyLoaded(true);
      setLoadingProgress({ current: 0, total: 0 });
    }
  }, [currentPage, pageSize, debouncedSearchQuery, collectionFilter, sortBy, sortOrder, hasInitiallyLoaded, browseAll, selectedOrganization?.organization_id]);

  // Load artworks on component mount and when filters change
  useEffect(() => {
    fetchArtworks();
  }, [fetchArtworks]);

  // Fetch pre-selected collection details when addToCollection URL param is present
  useEffect(() => {
    const fetchPreSelectedCollection = async () => {
      if (!addToCollectionId) {
        setPreSelectedCollection(null);
        return;
      }
      
      try {
        const { data: collection, error } = await supabase
          .from('collections')
          .select('collection_id, title, description')
          .eq('collection_id', addToCollectionId)
          .single();
        
        if (error) {
          console.error('Error fetching pre-selected collection:', error);
          setPreSelectedCollection(null);
        } else {
          setPreSelectedCollection(collection);
        }
      } catch (err) {
        console.error('Error fetching pre-selected collection:', err);
        setPreSelectedCollection(null);
      }
    };
    
    fetchPreSelectedCollection();
  }, [addToCollectionId]);

  // Debounce search query
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set a new timeout to update debounced value after 400ms
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);
    
    // Cleanup on unmount or when searchQuery changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, collectionFilter, sortBy, sortOrder]);

  const formatPrice = (price) => {
    if (!price || price === 0) return 'Not for sale';
    return `$${parseFloat(price).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Print quality helper functions
  const calculateQualityInfo = (width, height) => {
    const maxDim = Math.max(width || 0, height || 0);
    let qualityLevel = 'low';
    if (maxDim >= 4800) qualityLevel = 'excellent';
    else if (maxDim >= 3600) qualityLevel = 'good';
    else if (maxDim >= 2400) qualityLevel = 'acceptable';

    const availableSizes = [];
    Object.entries(PRINT_DIMENSIONS_240DPI).forEach(([size, dims]) => {
      if (maxDim >= dims.minPx) {
        availableSizes.push(size);
      }
    });

    return { qualityLevel, availableSizes };
  };

  const getQualityColor = (level) => {
    switch (level) {
      case 'excellent': return '#16a34a';
      case 'good': return '#2563eb';
      case 'acceptable': return '#ca8a04';
      case 'low': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Build Cloudinary analysis URL - works with both Cloudinary and external URLs
  const buildCloudinaryAnalysisUrl = (imageUrl) => {
    const cloudName = 'vibrantcast';
    if (imageUrl.includes('cloudinary.com') && imageUrl.includes('/image/upload/')) {
      let analysisUrl = imageUrl.replace('/image/upload/', '/image/upload/fl_getinfo/');
      if (!analysisUrl.includes('.json')) {
        analysisUrl = analysisUrl.replace(/\.[^/.]+$/, '.json');
      }
      return analysisUrl;
    } else {
      const encodedUrl = encodeURIComponent(imageUrl);
      return `https://res.cloudinary.com/${cloudName}/image/fetch/fl_getinfo/${encodedUrl}`;
    }
  };

  // Open print quality modal
  const openPrintModal = async (artwork) => {
    setPrintModalArtwork(artwork);
    setPrintModalOpen(true);
    setPrintStatus(null);
    setLoadingPrintStatus(true);

    try {
      const { data, error } = await supabase
        .from('artwork_print_status')
        .select('*')
        .eq('vc_id', artwork.vc_id)
        .is('optin_id', null)
        .maybeSingle();

      if (error) throw error;
      setPrintStatus(data);
    } catch (err) {
      console.error('Error loading print status:', err);
    } finally {
      setLoadingPrintStatus(false);
    }
  };

  const closePrintModal = () => {
    setPrintModalOpen(false);
    setPrintModalArtwork(null);
    setPrintStatus(null);
  };

  // Scan cover image quality via Cloudinary
  const scanCoverImageQuality = async () => {
    if (!printModalArtwork?.cover_image) return;

    setScanningQuality(true);

    try {
      const imageToScan = printStatus?.highres_image_url || printModalArtwork.cover_image;
      const infoUrl = buildCloudinaryAnalysisUrl(imageToScan);
      
      const response = await fetch(infoUrl);
      if (!response.ok) throw new Error('Failed to get image info');

      const data = await response.json();
      const { width, height, bytes } = data.input || data;
      const { qualityLevel, availableSizes } = calculateQualityInfo(width, height);

      // Save or update print status
      const { data: existing } = await supabase
        .from('artwork_print_status')
        .select('id, highres_image_url')
        .eq('vc_id', printModalArtwork.vc_id)
        .is('optin_id', null)
        .maybeSingle();

      const record = {
        vc_id: printModalArtwork.vc_id,
        optin_id: null,
        image_width: width,
        image_height: height,
        image_bytes: bytes,
        quality_level: qualityLevel,
        available_sizes: availableSizes,
        quality_checked_at: new Date().toISOString(),
        print_enabled: qualityLevel !== 'low'
      };

      if (existing?.highres_image_url) {
        record.highres_image_url = existing.highres_image_url;
      }

      if (existing) {
        await supabase.from('artwork_print_status').update(record).eq('id', existing.id);
      } else {
        await supabase.from('artwork_print_status').insert(record);
      }

      // Reload status
      const { data: updated } = await supabase
        .from('artwork_print_status')
        .select('*')
        .eq('vc_id', printModalArtwork.vc_id)
        .is('optin_id', null)
        .maybeSingle();
      
      setPrintStatus(updated);
    } catch (err) {
      console.error('Error scanning image:', err);
      alert('Failed to scan image: ' + err.message);
    } finally {
      setScanningQuality(false);
    }
  };

  // Upload high-res image
  const handlePrintHighResUpload = async (file) => {
    if (!file || !printModalArtwork) return;

    setUploadingHighRes(true);

    try {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 100) {
        throw new Error(`File too large (${fileSizeMB.toFixed(1)}MB). Maximum is 100MB.`);
      }

      const timestamp = Date.now();
      const extension = file.name.split('.').pop().toLowerCase();
      const fileName = `highres_${printModalArtwork.vc_id}_${timestamp}.${extension}`;

      const { error: uploadError } = await supabase.storage
        .from('highres_prints')
        .upload(fileName, file, { cacheControl: '3600', upsert: true, contentType: file.type });

      if (uploadError) throw new Error(uploadError.message);

      const { data: { publicUrl } } = supabase.storage
        .from('highres_prints')
        .getPublicUrl(fileName);

      // Wait for file to be available, then analyze via Cloudinary
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      let width = 0, height = 0, imageBytes = file.size;
      try {
        const cloudName = 'vibrantcast';
        const analysisUrl = `https://res.cloudinary.com/${cloudName}/image/fetch/fl_getinfo/${encodeURIComponent(publicUrl)}`;
        const response = await fetch(analysisUrl);
        if (response.ok) {
          const data = await response.json();
          const info = data.input || data;
          width = info.width;
          height = info.height;
          imageBytes = info.bytes || file.size;
        }
      } catch (e) {
        console.log('Cloudinary analysis failed, dimensions unknown');
      }

      const { qualityLevel, availableSizes } = calculateQualityInfo(width, height);

      const { data: existing } = await supabase
        .from('artwork_print_status')
        .select('id')
        .eq('vc_id', printModalArtwork.vc_id)
        .is('optin_id', null)
        .maybeSingle();

      const record = {
        vc_id: printModalArtwork.vc_id,
        optin_id: null,
        highres_image_url: publicUrl,
        image_width: width,
        image_height: height,
        image_bytes: imageBytes,
        quality_level: qualityLevel,
        available_sizes: availableSizes,
        quality_checked_at: new Date().toISOString(),
        print_enabled: qualityLevel !== 'low'
      };

      if (existing) {
        await supabase.from('artwork_print_status').update(record).eq('id', existing.id);
      } else {
        await supabase.from('artwork_print_status').insert(record);
      }

      // Reload status
      const { data: updated } = await supabase
        .from('artwork_print_status')
        .select('*')
        .eq('vc_id', printModalArtwork.vc_id)
        .is('optin_id', null)
        .maybeSingle();
      
      setPrintStatus(updated);
      alert(`High-res uploaded! ${width > 0 ? `${width}×${height}px (${qualityLevel})` : 'Analyzing...'}`);
    } catch (err) {
      console.error('Error uploading high-res:', err);
      alert('Upload failed: ' + err.message);
    } finally {
      setUploadingHighRes(false);
    }
  };

  // Analyze colors via Cloudinary
  const analyzeArtworkColors = async () => {
    const imageUrl = printStatus?.highres_image_url || printModalArtwork?.cover_image;
    if (!imageUrl) return;

    setAnalyzingColors(true);

    try {
      const analysisUrl = buildCloudinaryAnalysisUrl(imageUrl);
      const response = await fetch(analysisUrl);
      if (!response.ok) throw new Error('Failed to analyze');

      const data = await response.json();
      const colors = data.colors || data.predominant?.google || [];

      if (colors.length > 0) {
        const colorPalette = {};
        colors.slice(0, 6).forEach((colorInfo, index) => {
          const colorName = colorInfo[0] || `color_${index}`;
          colorPalette[colorName] = [{ hex: colorInfo[0], percentage: parseFloat(colorInfo[1] || 0) }];
        });

        await supabase
          .from('vcs')
          .update({ color_palette: colorPalette })
          .eq('vc_id', printModalArtwork.vc_id);

        alert(`Color analysis complete! Found ${colors.length} dominant colors.`);
      } else {
        alert('No dominant colors detected.');
      }
    } catch (err) {
      console.error('Error analyzing colors:', err);
      alert('Color analysis failed: ' + err.message);
    } finally {
      setAnalyzingColors(false);
    }
  };

  if (loading && !hasInitiallyLoaded) {
    return (
      <div className="curator-artworks">
        <LoadingSpinner />
        {loadingProgress.total > 0 && (
          <div className="loading-progress">
            Processing {loadingProgress.current} of {loadingProgress.total} artworks...
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="curator-artworks">
        <div className="error-message">
          <p>Error loading artworks: {error}</p>
          <button onClick={fetchArtworks} className="retry-button">
            <RefreshCw size={16} color="#6c757d" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-artworks">
      {/* Pre-selected Collection Banner */}
      {preSelectedCollection && (
        <div className="preselected-collection-banner">
          <div className="banner-content">
            <button 
              className="back-btn"
              onClick={() => navigate(`/curator/collections/${preSelectedCollection.collection_id}`)}
              title="Back to collection"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="banner-info">
              <span className="banner-label">Adding artworks to:</span>
              <span className="banner-title">{preSelectedCollection.title}</span>
            </div>
            <button 
              className="banner-done-btn"
              onClick={() => navigate(`/curator/collections/${preSelectedCollection.collection_id}`)}
            >
              Done
            </button>
          </div>
        </div>
      )}
      
      <div className="artworks-header">
        <div className="header-content">
          <h1>{browseAll ? 'Browse All Artworks' : 'Curated Artworks'}</h1>
          {browseAll && (
            <p className="header-subtitle">
              <Globe size={14} /> Browse all public artworks in the system to add to your collections
            </p>
          )}
        </div>
        
        {/* Toggle between Curated and Browse All */}
        <div className="browse-mode-toggle">
          <button
            className={`toggle-btn ${!browseAll ? 'active' : ''}`}
            onClick={() => {
              setBrowseAll(false);
              setCurrentPage(1);
              setHasInitiallyLoaded(false);
            }}
          >
            <BookOpen size={16} />
            Curated
          </button>
          <button
            className={`toggle-btn browse-all ${browseAll ? 'active' : ''}`}
            onClick={() => {
              setBrowseAll(true);
              setCurrentPage(1);
              setHasInitiallyLoaded(false);
            }}
          >
            <Globe size={16} />
            Browse All
          </button>
        </div>
        
        {/* Compact Stat Pills */}
        {stats && (
          <div className="header-stat-pills">
            <span className="stat-pill">
              <Image size={12} />
              {stats.total} {browseAll ? 'public' : 'artworks'}
            </span>
            {!browseAll && (
              <span className="stat-pill">
                <BookOpen size={12} />
                {stats.collections} collections
              </span>
            )}
            <span className="stat-pill">
              <DollarSign size={12} />
              ${Math.round(stats.avgPrice).toLocaleString()} avg
            </span>
          </div>
        )}
        
        <div className="header-actions">
          <div className="view-toggle">
            <button 
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 size={16} color="#6c757d" />
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <List size={16} color="#6c757d" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters Section - Two Row Layout */}
      <div className="artworks-filters-compact">
        {/* Row 1: Collection selector (only when not in browseAll mode) */}
        {!browseAll && (
          <div className="filter-row-1">
            <select
              value={collectionFilter}
              onChange={(e) => setCollectionFilter(e.target.value)}
              className="collection-select"
            >
              <option value="all">All Artworks</option>
              <option value="unassigned">Unassigned Artworks</option>
              {accessibleCollections.map(collection => (
                <option key={collection.collection_id} value={collection.collection_id}>
                  {collection.title}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* Row 2: Search + Sort + Refresh */}
        <div className="filter-row-2">
          <div className={`search-bar ${isSearching ? 'searching' : ''}`}>
            {isSearching ? (
              <RefreshCw size={14} color="#6c757d" className="search-spinner" />
            ) : (
              <Search size={14} color="#6c757d" />
            )}
            <input
              type="text"
              placeholder="Search artworks, artists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => setSearchQuery('')}
                type="button"
              >
                <X size={12} />
              </button>
            )}
          </div>
          
          <select 
            className="sort-select"
            value={`${sortBy}-${sortOrder}`} 
            onChange={(e) => {
              const [newSort, newOrder] = e.target.value.split('-');
              setSortBy(newSort);
              setSortOrder(newOrder);
            }}
          >
            <option value="created_at-desc">Newest</option>
            <option value="created_at-asc">Oldest</option>
            <option value="title-asc">A-Z</option>
            <option value="title-desc">Z-A</option>
            <option value="price-desc">Price ↓</option>
            <option value="price-asc">Price ↑</option>
          </select>
          
          <button onClick={fetchArtworks} className="refresh-btn">
            <RefreshCw size={14} color="#6c757d" />
          </button>
        </div>
      </div>

      {/* Artworks Content */}
      {artworks.length === 0 ? (
        <div className="no-artworks">
          <Image size={48} color="#6c757d" />
          <h3>No artworks found</h3>
          <p>No artworks found in your accessible collections.</p>
        </div>
      ) : (
        <>
          <div className={`artworks-container ${viewMode}`}>
            {viewMode === 'grid' ? (
              <div className="artworks-grid">
                {artworks.map((artwork) => (
                  <div 
                    key={artwork.vc_id}
                    className="artwork-card-new"
                  >
                    {/* Image Container */}
                    <div className="artwork-image-container" onClick={() => setFullscreenImage(artwork)}>
                      <img 
                        src={getDisplayImageUrl(artwork.cover_image)} 
                        alt={artwork.title}
                        className="artwork-image"
                        onError={(e) => {
                          e.target.src = '/images/placeholder-artwork.jpg';
                        }}
                      />
                      
                      {/* Minimal overlay badges */}
                      <div className="artwork-overlay">
                        <div className="overlay-icon">
                          <Eye size={24} color="#ffffff" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Compact Info Section */}
                    <div className="artwork-card-info">
                      <div className="artwork-header">
                        <h4 className="artwork-title">{artwork.title}</h4>
                        <p className="artwork-artist">{artwork.artist.nickname || artwork.artist.handle || 'Unknown'}</p>
                      </div>
                      
                      {/* Quick Stats Row */}
                      <div className="artwork-stats-row">
                        {artwork.width && artwork.height && (
                          <span className="dimensions">{artwork.width}"×{artwork.height}"</span>
                        )}
                        {artwork.price && (
                          <span className="price">{artwork.formattedPrice}</span>
                        )}
                        <span className="collections-count">
                          {artwork.collectionId ? '1 collection' : 'Unassigned'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons Row - Clean, horizontal layout */}
                    <div className="artwork-actions-row">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/stories/create?artworkId=${artwork.vc_id}`);
                        }}
                        className="action-btn story-btn"
                        title="Create Art Speaks story"
                      >
                        <BookOpen size={14} />
                        Create Story
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/curator/artworks/${artwork.vc_id}/print`);
                        }}
                        className="action-btn print-btn"
                        title="Check print quality"
                      >
                        <Printer size={14} />
                        Print
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDirectAssignment(artwork);
                        }}
                        className={`action-btn collection-btn ${preSelectedCollection ? 'primary' : ''}`}
                        title={preSelectedCollection ? `Add to ${preSelectedCollection.title}` : 'Add to collection'}
                        disabled={assigningCollectionId === preSelectedCollection?.collection_id}
                      >
                        <BookOpen size={14} />
                        {preSelectedCollection ? 'Add' : 'Add to Collection'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/curator/artworks/${artwork.vc_id}/edit`);
                        }}
                        className="action-btn edit-btn"
                        title="Edit artwork details"
                      >
                        <Edit3 size={14} />
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="artwork-table">
                <div className="table-header">
                  <div className="header-cell">Image</div>
                  <div className="header-cell">Title</div>
                  <div className="header-cell">Artist</div>
                  <div className="header-cell">Price</div>
                  <div className="header-cell">Collections</div>
                  <div className="header-cell">Actions</div>
                </div>
                
                {artworks.map((artwork) => (
                  <div key={artwork.vc_id} className="artwork-row">
                    <div className="cell-image" onClick={() => setFullscreenImage(artwork)}>
                      <img 
                        src={artwork.cover_image ? getDisplayImageUrl(artwork.cover_image) : '/images/placeholder-artwork.jpg'} 
                        alt={artwork.title}
                        onError={(e) => {
                          e.target.src = '/images/placeholder-artwork.jpg';
                        }}
                      />
                    </div>
                    <div className="cell-title">{artwork.title}</div>
                    <div className="cell-artist">{artwork.artist.nickname || artwork.artist.handle || 'Unknown'}</div>
                    <div className="cell-price">{artwork.formattedPrice}</div>
                    <div className="cell-collections">
                      <button 
                        className="collection-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCollectionSelector(artwork);
                        }}
                        title="Click to view/add to collections"
                      >
                        {artwork.collectionId ? '1 collection' : 'Unassigned'}
                      </button>
                    </div>
                    <div className="cell-actions">
                      <button 
                        className="action-btn story-btn" 
                        title="Create Story" 
                        onClick={() => navigate(`/stories/create?artworkId=${artwork.vc_id}`)}
                      >
                        <BookOpen size={14} color="#10b981" />
                      </button>
                      <button 
                        className="action-btn print-btn" 
                        title="Print Quality" 
                        onClick={() => navigate(`/curator/artworks/${artwork.vc_id}/print`)}
                      >
                        <Printer size={14} color="#8b5cf6" />
                      </button>
                      <button className="action-btn view-btn" title="View" onClick={() => handleArtworkClick(artwork.vc_id)}>
                        <Eye size={14} color="#007bff" />
                      </button>
                      <button
                        className="action-btn edit-btn"
                        title="Edit"
                        onClick={() => navigate(`/curator/artworks/${artwork.vc_id}/edit`)}
                      >
                        <Edit3 size={14} color="#6c757d" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalCount > pageSize && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount} artworks
              </div>
              
              <div className="pagination-controls">
                <button 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </button>
                
                <span className="page-info">
                  Page {currentPage} of {Math.ceil(totalCount / pageSize)}
                </span>
                
                <button 
                  disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
              
              <div className="page-size-selector">
                <label>Show:</label>
                <select 
                  value={pageSize} 
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                  <option value={96}>96</option>
                </select>
              </div>
            </div>
          )}
          
          {isPageLoading && (
            <div className="page-loading">
              <LoadingSpinner />
              <p>Loading more artworks...</p>
            </div>
          )}
        </>
      )}

      {/* Collections View Modal */}
      {collectionsViewOpen && selectedArtwork && (
        <div className="collections-view-modal" onClick={closeCollectionsView}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Collections</h3>
              <button className="close-btn" onClick={closeCollectionsView}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              {currentCollections.length > 0 ? (
                <div className="collections-list">
                  {currentCollections.map(collection => (
                    <div key={collection.collection_id} className="collection-item">
                      <div className="collection-info">
                        <h4>{collection.title}</h4>
                        {collection.description && <p>{collection.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-collections">This artwork is not in any collections yet.</p>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => {
                closeCollectionsView();
                openCollectionSelector(selectedArtwork);
              }}>
                Add to Collection
              </button>
              <button className="btn-secondary" onClick={closeCollectionsView}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection Selector Modal */}
      {collectionSelectorOpen && selectedArtwork && (
        <div className="collection-selector-modal" onClick={closeCollectionSelector}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add to Collection</h3>
              <button className="close-btn" onClick={closeCollectionSelector}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              {collectionSelectorError && (
                <div className="modal-error">
                  {collectionSelectorError}
                </div>
              )}

              {accessibleCollections.length > 0 ? (
                <div className="collections-selector-list">
                  {accessibleCollections.map(collection => {
                    const isAssigned = currentCollections.some(
                      (assigned) => Number(assigned.collection_id) === Number(collection.collection_id)
                    );
                    const isLoading = assigningCollectionId === collection.collection_id;

                    return (
                      <button
                        key={collection.collection_id}
                        className={`collection-selector-button${isAssigned ? ' assigned' : ''}`}
                        onClick={() => handleAssignToCollection(collection.collection_id)}
                        disabled={isLoading}
                      >
                        <span className="collection-title">{collection.title}</span>
                        {isAssigned && <span className="collection-status">Already assigned</span>}
                        {isLoading && <span className="collection-status">Adding...</span>}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="no-collections">You don't have access to any collections.</p>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeCollectionSelector} disabled={Boolean(assigningCollectionId)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div 
          className="fullscreen-image-modal" 
          onClick={() => setFullscreenImage(null)}
        >
          <button 
            className="fullscreen-close-btn" 
            onClick={() => setFullscreenImage(null)}
            aria-label="Close fullscreen"
          >
            <X size={28} />
          </button>
          
          <div className="fullscreen-image-container">
            <img 
              src={getDisplayImageUrl(fullscreenImage.cover_image)} 
              alt={fullscreenImage.title}
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                e.target.src = '/images/placeholder-artwork.jpg';
              }}
            />
          </div>
          
          <div className="fullscreen-info" onClick={(e) => e.stopPropagation()}>
            <h2 className="fullscreen-title">{fullscreenImage.title}</h2>
            <p className="fullscreen-artist">
              {fullscreenImage.artist?.nickname || fullscreenImage.artist?.handle || 'Unknown Artist'}
            </p>
            {fullscreenImage.dimensions && (
              <p className="fullscreen-dimensions">{fullscreenImage.dimensions}</p>
            )}
            {fullscreenImage.formattedPrice && fullscreenImage.formattedPrice !== 'Not for sale' && (
              <p className="fullscreen-price">{fullscreenImage.formattedPrice}</p>
            )}
          </div>
        </div>
      )}

      {/* Print Quality Modal */}
      {printModalOpen && printModalArtwork && (
        <div className="print-quality-modal" onClick={closePrintModal}>
          <div className="modal-content print-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                <Printer size={20} />
                <h3>Print Quality Check</h3>
              </div>
              <button className="close-btn" onClick={closePrintModal}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body print-modal-body">
              {/* Artwork Preview */}
              <div className="print-artwork-preview">
                <img 
                  src={getDisplayImageUrl(printModalArtwork.cover_image)} 
                  alt={printModalArtwork.title}
                  onError={(e) => { e.target.src = '/images/placeholder-artwork.jpg'; }}
                />
                <div className="artwork-info">
                  <h4>{printModalArtwork.title}</h4>
                  <p>{printModalArtwork.artist?.nickname || printModalArtwork.artist?.handle || 'Unknown Artist'}</p>
                </div>
              </div>

              {/* Print Status */}
              {loadingPrintStatus ? (
                <div className="print-loading">
                  <RefreshCw className="spin" size={20} />
                  <span>Loading print status...</span>
                </div>
              ) : printStatus ? (
                <div className="print-status-info">
                  <div className="status-header">
                    <span 
                      className="quality-badge"
                      style={{ backgroundColor: getQualityColor(printStatus.quality_level) }}
                    >
                      <Check size={12} />
                      {printStatus.quality_level}
                    </span>
                    {printStatus.print_enabled && (
                      <span className="enabled-badge">
                        <Printer size={12} /> Print Enabled
                      </span>
                    )}
                  </div>
                  
                  <div className="status-details">
                    <div className="detail-row">
                      <span className="label">Dimensions:</span>
                      <span className="value">
                        {printStatus.image_width?.toLocaleString()} × {printStatus.image_height?.toLocaleString()}px
                      </span>
                    </div>
                    {printStatus.image_bytes && (
                      <div className="detail-row">
                        <span className="label">File Size:</span>
                        <span className="value">{formatFileSize(printStatus.image_bytes)}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="label">Last Checked:</span>
                      <span className="value">
                        {printStatus.quality_checked_at 
                          ? new Date(printStatus.quality_checked_at).toLocaleDateString() 
                          : 'Never'}
                      </span>
                    </div>
                  </div>

                  {/* Available Sizes */}
                  {printStatus.available_sizes?.length > 0 && (
                    <div className="available-sizes">
                      <h5>Available Print Sizes (240 DPI)</h5>
                      <div className="sizes-grid">
                        {Object.entries(PRINT_DIMENSIONS_240DPI).map(([size, dims]) => {
                          const isAvailable = printStatus.available_sizes.includes(size);
                          return (
                            <span 
                              key={size} 
                              className={`size-tag ${isAvailable ? 'available' : 'unavailable'}`}
                            >
                              {isAvailable ? <Check size={10} /> : <X size={10} />}
                              {dims.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* High-res link */}
                  {printStatus.highres_image_url && (
                    <div className="highres-link">
                      <a 
                        href={printStatus.highres_image_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-link"
                      >
                        <ExternalLink size={14} />
                        View High-Res Image
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-print-status">
                  <FileImage size={32} />
                  <p>No print quality data yet.</p>
                  <p className="hint">Scan the cover image to check print eligibility.</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="print-actions">
                <input
                  type="file"
                  ref={highResInputRef}
                  style={{ display: 'none' }}
                  accept="image/jpeg,image/png,image/tiff,image/heic,image/heif,image/webp"
                  onChange={(e) => handlePrintHighResUpload(e.target.files?.[0])}
                />
                
                <button
                  className="btn-primary"
                  onClick={() => highResInputRef.current?.click()}
                  disabled={uploadingHighRes}
                >
                  {uploadingHighRes ? (
                    <><RefreshCw className="spin" size={16} /> Uploading...</>
                  ) : (
                    <><Upload size={16} /> {printStatus?.highres_image_url ? 'Replace High-Res' : 'Upload High-Res'}</>
                  )}
                </button>
                
                <button
                  className="btn-secondary"
                  onClick={scanCoverImageQuality}
                  disabled={scanningQuality}
                >
                  {scanningQuality ? (
                    <><RefreshCw className="spin" size={16} /> Scanning...</>
                  ) : (
                    <><Zap size={16} /> {printStatus?.highres_image_url ? 'Re-Scan' : 'Scan Cover Image'}</>
                  )}
                </button>
                
                <button
                  className="btn-secondary"
                  onClick={analyzeArtworkColors}
                  disabled={analyzingColors}
                >
                  {analyzingColors ? (
                    <><RefreshCw className="spin" size={16} /> Analyzing...</>
                  ) : (
                    <><Palette size={16} /> Analyze Colors</>
                  )}
                </button>
              </div>

              <div className="print-notes">
                <p><strong>Workflow:</strong> Scan cover image first. If quality is low, upload a high-res version.</p>
                <p><strong>For 20" prints:</strong> Need 4,800px minimum on longest side (240 DPI)</p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => {
                  closePrintModal();
                  navigate(`/curator/artworks/${printModalArtwork.vc_id}/edit`);
                }}
              >
                <Edit3 size={14} />
                Edit Full Details
              </button>
              <button className="btn-secondary" onClick={closePrintModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuratorArtworks;
